import { DateISO } from '@agro-club/agroclub-shared'
import { Company } from 'modules/domain/company/types'
import { Address, Coordinator, DocumentFile, Product, RespFile } from 'modules/domain/types'
import { Profile, User } from 'modules/domain/user/types'

export enum SampleStatus {
  in_progress = 'in_progress',
  published = 'published',
  archived = 'archived',
  canceled = 'canceled',
}

export enum SampleReactionStatus {
  norm = 'norm',
  expired = 'expired',
  paused = 'paused',
  reacted = 'reacted',
}

export type Owner = Omit<User, 'profile'> & {
  profile: Profile
}

export type SampleBase = {
  id: number
  address: Address
  bid: SampleBid
  created: string
  created_by: {
    full_name: string
    id: string
    is_active: boolean
    phone: string
  }
  expire_at: DateISO | undefined // probably will be deleted soon
  modified: string
  published_at: string | null
  owner: Owner
  product: Product
  status: SampleStatus
  title: string
  files: RespFile[]
}

export type SamplePartParam = {
  id: number
  parameter: {
    id: number
    name: string
    units?: string
  }
  parameter_value?: number
  comment: string
  parameter_group_type: string
  parameter_group_main_parameter: boolean
}

export type SampleForList = SampleBase & {
  request_status: string
  request_current_responsible: Coordinator
  request_delay_comment?: string
  sent_to_export_bot_date: string
  reaction_status: string
  pause_reason?: string
  time_left_for_reaction?: number // in minutes
  react_until?: string
}

export type SampleWithRequests = SampleBase & {
  sample_requests: SampleRequestType[]

  sent_to_export_bot_date: string
  can_be_send_to_export_bot_date: string
  cant_be_send_to_export_bot_reason: string
  can_be_send_to_export_bot: boolean

  sent_to_local_bot_date: string
  can_be_send_to_local_bot: boolean
  cant_be_send_to_local_bot_reason?: string
}

export type SampleBid = {
  id: number
  company: Company
  owner: number
  owner_is_significant?: boolean
  owner_is_verified_by_coordinator?: boolean
  owner_is_verified_by_security?: boolean
  owner_is_verified_by_kyc?: boolean
  owner_is_verified?: boolean
}

export type SampleDetailed = {
  sample_request_id: number
  sample_request_status: string

  sent_to_export_bot_date: string
  can_be_send_to_export_bot_date: string
  can_be_send_to_export_bot: boolean
  cant_be_send_to_export_bot_reason?: string

  sent_to_local_bot_date: string
  can_be_send_to_local_bot: boolean
  cant_be_send_to_local_bot_reason?: string
  reaction_status: string
  pause_reason: string
  react_until?: string
  time_left_for_reaction?: number // in minutes
} & SampleBase

export enum LaboratoryCodes {
  voronezh = 'voronezh',
  rostov = 'rostov',
  other = 'other',
}

export type Lab = {
  id: string
  name: string
  code: LaboratoryCodes
  senior_lab_assistant?: Coordinator
}

export enum SampleRequestDocsType {
  regional_files = 'regional_files',
  delivery_docs = 'delivery_docs',
  lab_files = 'lab_files',
  export_files = 'export_files',
  local_files = 'local_files',
}

export enum AnalysisTypes {
  gost = 'gost',
  iso = 'iso',
  en = 'en',
  isiri = 'isiri',
}

export enum SamplesProgressStatuses {
  CREATED = 'created',
  ASSIGNED_TO_REGIONAL = 'assigned_to_regional',
  ASSIGNED_TO_AGENT = 'assigned_to_agent',
  ACCEPTED_BY_REGIONAL = 'accepted_by_regional',
  REGIONAL_SENT_FILES = 'regional_sent_files',
  REGIONAL_SENT_SAMPLES = 'regional_sent_samples',
  DELIVERED_TO_LAB = 'delivered_to_lab',
  ACCEPTED_BY_LAB = 'accepted_by_lab',
  COMPLETED = 'completed',
  CANCELED = 'canceled',
}

export type SampleRequestType = {
  id: string
  deal: string
  bid: string
  sample: string
  regional: Coordinator | null
  senior_regional: Coordinator
  created: string
  modified: string
  quantity: number
  product: Product
  seller_user: User
  seller_address: Address & { street_house_address: string }
  phone: string
  lab_assistant: Coordinator | null
  laboratory: LaboratoryCodes | null
  lab_comment: string | null
  comment: string | null
  created_by: Coordinator
  status: SamplesProgressStatuses
  sample_delivery_comment: string | null
  sample_delivery_method: string | null
  sample_valid_days: number | null
  documents: DocumentFile[]
  analysis_type?: AnalysisTypes
  cancel_comment: string | null
  warehouse_location?: string
  warehouse_location_comment?: string
  scales_location?: string
  scales_location_comment?: string
  updates: {
    created: string
    status: SamplesProgressStatuses
    status_comment?: string
    status_delay_comment?: string
  }[]
}

export type SampleCell = {
  color_group?: string
  text: string
}

export type SampleHeaderCell = {
  color_group?: string
  text: string
  field_id: string
}

export type SamplePartsTab = {
  title: string
  headers: SampleHeaderCell[]
  data: SampleCell[][]
}
